// src/tenantAdmin/pages/CompanyWorkspacesPage.jsx
//
// The "Workspace" tab of Gestione Workspace: the workspaces the company owns,
// how many of its workspace seats they take, and the one write a company admin
// may make here — creating another one.
//
// ⛔ Reaching this page already proves the guard chain (RequireWorkspaceAdmin then
// RequireCustomerAdmin), so it may assume A company claim — but NEVER a specific
// company id. That comes from the layout's Outlet context and is passed through
// `isCustomerAdminOf` before ANY request: an id the user does not administer (the
// selector is still resolving, or a claims change dropped it) answers 403
// CUSTOMER_ADMIN_REQUIRED, which raises the global permissions-drift banner and
// tells the user their rights changed when in fact WE aimed at the wrong company.
//
// ⛔⛔ `include_inactive=true` (companyApi's default) is deliberate: a RETIRED
// workspace still occupies a workspace seat, so a list that hid them would make
// `seats_used` look wrong. They are shown with a "Ritirato" chip instead. This
// page cannot retire or delete one — on `dev` that is superadmin-only.
//
// ⛔⛔ Creating a workspace does NOT make it usable in this session. `tenants[]`
// is minted at sign-in and `dev` serves no route that re-mints it, so the new
// workspace is absent from the Utenti tab's selector (and from everything else)
// until the next sign-in. The success outcome SAYS SO and offers the remedy as a
// BUTTON — ⛔ never auto-triggered: `reauthenticate()` is a sign-out on `dev`, and
// ejecting someone right after a successful write would look like a failure.
//
// ⭐ RENAME lives here (moved from the Utenti tab, 2026-09-26): one "Rinomina" per
// row, through PATCH /tenant-admin/tenant?tenant=<id>. That route is gated on the
// SIGN-IN CLAIMS (`tenants[].role === 'admin'`), so the button is enabled only for
// a workspace in `adminTenants`: a retired one gets no button (it is not in the
// token), and one created in this session is disabled with a tooltip until the
// next sign-in. ⚠️ A workspace admin who is NOT a company admin cannot reach this
// tab and therefore cannot rename — a product decision, not an oversight.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, Pencil } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { listCompanyWorkspaces, createCompanyWorkspace } from '../companyApi.js';
import { renameTenant } from '../tenantAdminApi.js';
import { userActionErrorCopy } from '../userActionError.js';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import RenameWorkspaceDialog from '../components/RenameWorkspaceDialog.jsx';
import { COLORS, FONT_SANS, FONT_DISPLAY, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';

// "Not told": what the seats line reads before the envelope lands, without a
// company, and when the read fails. ⛔ `used: null` is UNKNOWN, never zero — it
// hides the figure rather than printing one nobody stands behind (same rule as
// the Utenti tab's SeatTile).
const SEATS_UNKNOWN = { used: null, limit: null, source: null };

const th = {
  padding: '12px 24px', textAlign: 'left',
  fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px', textTransform: 'uppercase',
  color: COLORS.textSecondary, fontFamily: FONT_SANS, whiteSpace: 'nowrap',
};
const td = {
  padding: '14px 24px', fontSize: '13px', color: COLORS.ink,
  fontFamily: FONT_SANS, borderTop: `1px solid ${COLORS.border}`,
};
const fieldBase = {
  height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink,
  fontSize: '13px', fontFamily: FONT_SANS, outline: 'none',
};

export default function CompanyWorkspacesPage() {
  const { t } = useTranslation('admin');
  const { company } = useOutletContext();
  const { isCustomerAdminOf, reauthenticate, adminTenants, renameTenantLocally } = useAuth();

  const companyId = company?.companyId || null;
  // /billing acts on the company owning the ACTIVE workspace. A link to it is
  // offered only when that is the company on screen; unknown → no link.
  const billingIsThisCompany = !!companyId && company?.activeCompanyId === companyId;
  // The workspaces the rename route will accept: those in the sign-in claims.
  const renamable = new Set((adminTenants || []).map(w => w.tenant_id));
  // ⛔ THE gate. Everything below reads it before touching the network.
  const allowed = isCustomerAdminOf(companyId);
  // The company on screen NOW: a create that answers after a switch belongs to the
  // old company — no outcome, no re-read over the new one.
  const companyRef = useRef(companyId);
  companyRef.current = companyId;

  const [rows, setRows] = useState([]);
  const [seats, setSeats] = useState(SEATS_UNKNOWN);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  // The refusal of a create, as a sentence WE wrote plus whether the remedy is a
  // link. ⛔ Never `err.message` — see userActionError.js.
  const [createError, setCreateError] = useState(null);
  // What the last write did (OutcomeDialog). Its only action is the re-login offer.
  const [outcome, setOutcome] = useState(null);
  // The row whose rename dialog is open — the write lands on THAT workspace.
  const [renameTarget, setRenameTarget] = useState(null);

  // One ticket per read: the company selector sits above this page, so two loads
  // can be in flight at once and the slow one is not always the old one. A late
  // answer for company A landing under company B's name would show one company's
  // workspaces as another's. Anything that is not the latest is dropped.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!allowed) {
      // No claim, no request — and no stale rows left on screen either.
      setRows([]);
      setSeats(SEATS_UNKNOWN);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listCompanyWorkspaces(companyId);
      if (mine !== seq.current) return;
      setRows(data?.tenants || []);
      setSeats({
        used: data?.seats_used ?? null,
        limit: data?.seat_limit ?? null,
        source: data?.seat_source ?? null,
      });
    } catch (err) {
      if (mine !== seq.current) return;
      setRows([]);
      setSeats(SEATS_UNKNOWN);
      setError(userActionErrorCopy(t, err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [allowed, companyId, t]);

  // A company change clears what belongs to the previous one before the next
  // answer arrives — keeping the old rows "until the new ones land" is the same
  // defect held for a shorter time.
  useEffect(() => {
    setRows([]);
    setSeats(SEATS_UNKNOWN);
    setOutcome(null);
    setCreateError(null);
    setName('');
    setRenameTarget(null);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // The four refusals of POST /tenants that have copy of their own. Everything
  // else falls through to userActionErrorCopy, which ends at
  // `tenantUsers.genericError` with the code — a handle for support.
  //
  // ⛔⛔ SEAT_LIMIT_REACHED carries `{used, limit, source}` under `details`, NOT at
  // the top level. Reading `err.source` yields undefined and silently picks the
  // WRONG remedy: `plan` is a cap money can lift (link to Piani), `customer` is a
  // ceiling an operator set for this company and no purchase changes it, so
  // offering an upgrade there sends the admin to buy something useless.
  // An unknown/absent source takes the `customer` sentence WITHOUT the link: the
  // conservative half is "talk to us", never "go and pay".
  function createRefusal(err) {
    if (err?.code === 'SEAT_LIMIT_REACHED') {
      const source = err?.details?.source;
      if (source === 'plan') return { text: t('manage.workspaces.atCapPlan'), billingLink: true };
      return { text: t('manage.workspaces.atCapCustomer'), billingLink: false };
    }
    if (err?.code === 'CUSTOMER_HAS_NO_PLAN') return { text: t('manage.workspaces.noPlan'), billingLink: false };
    if (err?.code === 'CUSTOMER_INACTIVE') return { text: t('manage.workspaces.inactive'), billingLink: false };
    if (err?.code === 'CUSTOMER_DATA_INVALID') return { text: t('manage.workspaces.invalid'), billingLink: false };
    return { text: userActionErrorCopy(t, err), billingLink: false };
  }

  async function handleCreate(e) {
    e.preventDefault();
    const next = name.trim();
    if (!allowed || creating || next === '') return;
    setCreateError(null);
    setOutcome(null);
    setCreating(true);
    const at = companyId;
    try {
      await createCompanyWorkspace(at, next);
      if (companyRef.current !== at) return;
      setName('');
      // The row and the seat figure both moved; re-read rather than patch them in.
      await load();
      setOutcome({
        tone: 'success',
        text: t('manage.workspaces.created', { name: next }),
        // ⛔ Offered, NEVER called for them: reauthenticate() signs the user out.
        action: { label: t('manage.workspaces.reloginNow'), onClick: reauthenticate },
      });
    } catch (err) {
      if (companyRef.current === at) setCreateError(createRefusal(err));
    } finally {
      setCreating(false);
    }
  }

  // ⚠️ TWO writes, in this order and both required: the backend renames the
  // workspace (and re-syncs its brand-identity company name), then the local
  // claims copy is patched so the header, the Utenti selector and every other
  // reader stop showing the old name. `tenants[]` is minted at sign-in and `dev`
  // has no claims-refresh route, so a reload shows the old name again until the
  // next sign-in — which is what manage.renameDone says.
  async function submitRename(name) {
    const target = renameTarget;
    await renameTenant(target.id, name);
    renameTenantLocally(target.id, name);
    // After the re-read, so the outcome opens as the rename dialog closes.
    await load();
    setOutcome({ tone: 'success', text: t('manage.renameDone') });
  }

  // `seat_source: 'unlimited'` is a figure with no ceiling even when a number
  // rides along, so it reads as the uncapped sentence.
  const seatsKnown = seats.used != null;
  const hasCap = seats.limit != null && seats.source !== 'unlimited';

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1280px', margin: '0 auto' }}>

      {error && (
        <div role="alert" style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.control,
          background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
          color: SEMANTIC.errore.fg, fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px', flexWrap: 'wrap' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
            border: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Building2 size={20} color={COLORS.ink} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '16px', fontWeight: 700, lineHeight: '24px' }}>
              {t('manage.workspaces.title')}
            </h2>
            {/* The workspace seats of the company — NOT its people (that cap is
                the Utenti tab's). Hidden while unknown: a wrong figure here is
                worse than none. */}
            {seatsKnown && (
              <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>
                {hasCap
                  ? t('manage.workspaces.seats', { used: seats.used, limit: seats.limit })
                  : t('manage.workspaces.seatsNoCap', { used: seats.used })}
              </div>
            )}
          </div>
        </div>

        {allowed && (
          <>
            {/* Create. 1–200 characters backend-side; the 1-char end is the
                submit gate, `maxLength` mirrors the other. */}
            <form onSubmit={handleCreate} style={{ padding: '0 24px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <label
                    htmlFor="cwp-name"
                    style={{
                      display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
                      textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: '8px',
                    }}
                  >
                    {t('manage.workspaces.createName')}
                  </label>
                  <input
                    id="cwp-name"
                    type="text"
                    value={name}
                    maxLength={200}
                    onChange={e => setName(e.target.value)}
                    style={{ ...fieldBase, width: '100%', padding: '0 14px' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={creating || name.trim() === ''}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    height: '46px', padding: '0 20px', borderRadius: RADII.control, border: 'none',
                    background: (creating || name.trim() === '') ? COLORS.borderStrong : COLORS.accent,
                    color: COLORS.surface, fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS,
                    cursor: (creating || name.trim() === '') ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Plus size={16} /> {t('manage.workspaces.create')}
                </button>
              </div>

              {createError && (
                <div role="alert" style={{
                  marginTop: '12px', padding: '10px 14px', borderRadius: RADII.controlInner,
                  background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
                  color: SEMANTIC.errore.fg, fontSize: '13px',
                  display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                }}>
                  <span>{createError.text}</span>
                  {/* Only a PLAN cap is worth a link, and only when the plans
                      screen would open THIS company's plans: it acts on the
                      company owning the ACTIVE workspace, so for an admin of two
                      companies looking at the other one the sentence stands
                      alone (same rule as SeatTile's showBillingLink). The link
                      stays inside the console — `billingIsThisCompany` is
                      exactly the Piani tab's own guard, so it always lands on
                      content. */}
                  {createError.billingLink && billingIsThisCompany && (
                    <Link
                      to="/tenant-admin/manage/plans"
                      style={{ fontWeight: 700, color: COLORS.accent, textDecoration: 'underline' }}
                    >
                      {t('seats.upgradeLink')}
                    </Link>
                  )}
                </div>
              )}
            </form>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: COLORS.field }}>
                  <th style={th}>{t('shared.name')}</th>
                  <th style={th}>{t('users.columns.createdAt')}</th>
                  <th style={th} aria-label={t('manage.rename')} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={3}>
                      {t('shared.loading')}
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td style={{ ...td, textAlign: 'center', color: COLORS.textTertiary }} colSpan={3}>
                      {t('shared.emptyResult')}
                    </td>
                  </tr>
                )}
                {!loading && rows.map(row => (
                  <tr key={row.id}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span>{row.display_name || row.id}</span>
                        {/* ⚠️ Retired, not gone: the row still holds a workspace
                            seat, which is why it is listed at all. */}
                        {row.is_active === false && (
                          <span style={{
                            padding: '2px 10px', borderRadius: RADII.pill,
                            background: SEMANTIC.neutro.bg, color: SEMANTIC.neutro.fg,
                            fontSize: '11px', fontWeight: 600,
                          }}>
                            {t('manage.workspaces.retired')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={td}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {/* A retired workspace is not in the token: no button at all. */}
                      {row.is_active !== false && (
                        <button
                          type="button"
                          onClick={() => { if (renamable.has(row.id)) setRenameTarget(row); }}
                          disabled={!renamable.has(row.id)}
                          title={renamable.has(row.id) ? undefined : t('manage.renameAfterSignIn')}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            height: '34px', padding: '0 14px', borderRadius: RADII.control,
                            border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
                            color: COLORS.ink, fontSize: '12px', fontWeight: 600, fontFamily: FONT_SANS,
                            whiteSpace: 'nowrap',
                            cursor: renamable.has(row.id) ? 'pointer' : 'not-allowed',
                            opacity: renamable.has(row.id) ? 1 : 0.5,
                          }}
                        >
                          <Pencil size={13} /> {t('manage.rename')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <RenameWorkspaceDialog
        open={!!renameTarget}
        currentName={renameTarget?.display_name}
        onSubmit={submitRename}
        onClose={() => setRenameTarget(null)}
      />

      <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} />
    </div>
  );
}
